import Decimal from "decimal.js";

import { canonicalizeCategory, isPrintable } from "@/lib/processing/normalize";
import type { CatalogKitConfig, NormalizedRow, QualityIssue, Severity } from "@/types";

/**
 * Data-quality detection rules, mirroring the Python `reports/quality.py`. Each
 * rule is a pure function `(rows, config) => QualityIssue[]` and runs on *mapped*
 * rows (raw vendor SKU preserved, `last_update` parsed) so it can observe issues
 * that later normalization would erase. Rules never mutate input and never block
 * processing; they only report.
 *
 * The only impurity is {@link detectStalePricing}, which compares against a
 * `now` timestamp — injectable for deterministic tests.
 */

/** Resolve a rule's configured severity, defaulting to `warning`. */
function severityFor(config: CatalogKitConfig, ruleId: string): Severity {
  return config.quality.rules[ruleId]?.severity ?? "warning";
}

/** Canonicalized category for reporting / threshold lookup. */
function categoryOf(row: NormalizedRow): string {
  return canonicalizeCategory(row.category);
}

/** Margin percent for a mapped row, or `null` when price is not positive. */
function marginPercentOf(row: NormalizedRow): Decimal | null {
  const price = new Decimal(row.price);
  if (price.lte(0)) return null;
  const cost = new Decimal(row.cost);
  return price.minus(cost).div(price).times(100);
}

/** Number of days in a (zero-based) month of a given year. */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** Shift `moment` back by `months` calendar months (clamping the day). */
function shiftMonthsBack(moment: Date, months: number): Date {
  const zeroBased = moment.getFullYear() * 12 + moment.getMonth() - months;
  const year = Math.floor(zeroBased / 12);
  const month = zeroBased - year * 12;
  const day = Math.min(moment.getDate(), daysInMonth(year, month));
  return new Date(year, month, day, moment.getHours(), moment.getMinutes(), moment.getSeconds());
}

/** Rule 1 — price is zero while cost is non-zero. */
export function detectZeroPriceNonzeroCost(
  rows: NormalizedRow[],
  config: CatalogKitConfig,
): QualityIssue[] {
  const severity = severityFor(config, "zero_price_nonzero_cost");
  const issues: QualityIssue[] = [];
  for (const row of rows) {
    const price = new Decimal(row.price);
    const cost = new Decimal(row.cost);
    if (price.isZero() && cost.gt(0)) {
      issues.push({
        rule_id: "zero_price_nonzero_cost",
        severity,
        product_id: row.product_id,
        category: categoryOf(row),
        message: "Price is zero but cost is non-zero.",
        details: { price: row.price, cost: row.cost },
      });
    }
  }
  return issues;
}

/** Rule 2 — positive price but zero cost (missing cost data). */
export function detectNonzeroPriceZeroCost(
  rows: NormalizedRow[],
  config: CatalogKitConfig,
): QualityIssue[] {
  const severity = severityFor(config, "nonzero_price_zero_cost");
  const issues: QualityIssue[] = [];
  for (const row of rows) {
    const price = new Decimal(row.price);
    const cost = new Decimal(row.cost);
    if (price.gt(0) && cost.isZero()) {
      issues.push({
        rule_id: "nonzero_price_zero_cost",
        severity,
        product_id: row.product_id,
        category: categoryOf(row),
        message: "Price is non-zero but cost is zero.",
        details: { price: row.price, cost: row.cost },
      });
    }
  }
  return issues;
}

/** Rule 3 — margin percent below the (per-category) threshold. */
export function detectLowMargin(rows: NormalizedRow[], config: CatalogKitConfig): QualityIssue[] {
  const severity = severityFor(config, "low_margin");
  const thresholds = config.quality.category_thresholds;
  const defaultThreshold = config.quality.low_margin_threshold_percent;
  const issues: QualityIssue[] = [];
  for (const row of rows) {
    const margin = marginPercentOf(row);
    if (margin === null) continue;
    const category = categoryOf(row);
    const threshold = thresholds[category] ?? defaultThreshold;
    if (margin.lt(threshold)) {
      issues.push({
        rule_id: "low_margin",
        severity,
        product_id: row.product_id,
        category,
        message: `Margin ${margin.toFixed(2)}% is below threshold ${threshold}%.`,
        details: { margin_percent: margin.toFixed(2), threshold_percent: String(threshold) },
      });
    }
  }
  return issues;
}

/** Rule 4 — negative margin percent (price below cost). */
export function detectNegativeMargin(
  rows: NormalizedRow[],
  config: CatalogKitConfig,
): QualityIssue[] {
  const severity = severityFor(config, "negative_margin");
  const issues: QualityIssue[] = [];
  for (const row of rows) {
    const margin = marginPercentOf(row);
    if (margin !== null && margin.lt(0)) {
      issues.push({
        rule_id: "negative_margin",
        severity,
        product_id: row.product_id,
        category: categoryOf(row),
        message: `Margin is negative (${margin.toFixed(2)}%).`,
        details: { margin_percent: margin.toFixed(2), price: row.price, cost: row.cost },
      });
    }
  }
  return issues;
}

/** Rule 5 — negative on-hand quantity. */
export function detectNegativeInventory(
  rows: NormalizedRow[],
  config: CatalogKitConfig,
): QualityIssue[] {
  const severity = severityFor(config, "negative_inventory");
  const issues: QualityIssue[] = [];
  for (const row of rows) {
    if (row.on_hand < 0) {
      issues.push({
        rule_id: "negative_inventory",
        severity,
        product_id: row.product_id,
        category: categoryOf(row),
        message: `On-hand quantity is negative (${row.on_hand}).`,
        details: { on_hand: row.on_hand },
      });
    }
  }
  return issues;
}

/** Rule 6 — last update older than the configured month window. */
export function detectStalePricing(
  rows: NormalizedRow[],
  config: CatalogKitConfig,
  now: Date,
): QualityIssue[] {
  const severity = severityFor(config, "stale_pricing");
  const months = config.quality.stale_pricing_months;
  const cutoff = shiftMonthsBack(now, months);
  const issues: QualityIssue[] = [];
  for (const row of rows) {
    if (row.last_update === null) continue;
    const updated = new Date(row.last_update);
    if (Number.isNaN(updated.getTime())) continue;
    if (updated.getTime() < cutoff.getTime()) {
      issues.push({
        rule_id: "stale_pricing",
        severity,
        product_id: row.product_id,
        category: categoryOf(row),
        message: `Pricing last updated ${row.last_update.slice(0, 10)} (older than ${months} months).`,
        details: { last_update: row.last_update, months_threshold: months },
      });
    }
  }
  return issues;
}

/** Rule 7 — manufacturer is blank or the catch-all "Other". */
export function detectMissingManufacturer(
  rows: NormalizedRow[],
  config: CatalogKitConfig,
): QualityIssue[] {
  const severity = severityFor(config, "missing_manufacturer");
  const issues: QualityIssue[] = [];
  for (const row of rows) {
    const manufacturer = (row.manufacturer ?? "").trim();
    if (manufacturer === "" || manufacturer.toLowerCase() === "other") {
      issues.push({
        rule_id: "missing_manufacturer",
        severity,
        product_id: row.product_id,
        category: categoryOf(row),
        message: "Manufacturer is missing or 'Other'.",
        details: { manufacturer: row.manufacturer },
      });
    }
  }
  return issues;
}

/** Rule 8 — raw vendor SKU has edge whitespace or non-printable characters. */
export function detectDirtyVendorSku(
  rows: NormalizedRow[],
  config: CatalogKitConfig,
): QualityIssue[] {
  const severity = severityFor(config, "dirty_vendor_sku");
  const issues: QualityIssue[] = [];
  for (const row of rows) {
    const raw = row.vendor_sku;
    if (raw === null) continue;
    const hasEdgeWhitespace = raw !== raw.trim();
    const hasNonprintable = [...raw].some((char) => !isPrintable(char));
    if (hasEdgeWhitespace || hasNonprintable) {
      issues.push({
        rule_id: "dirty_vendor_sku",
        severity,
        product_id: row.product_id,
        category: categoryOf(row),
        message:
          "Vendor SKU has leading/trailing whitespace or non-printable characters.",
        details: {
          raw,
          has_edge_whitespace: hasEdgeWhitespace,
          has_nonprintable: hasNonprintable,
        },
      });
    }
  }
  return issues;
}

/** Rule 9 — product ID appears on more than one row. */
export function detectDuplicateProductId(
  rows: NormalizedRow[],
  config: CatalogKitConfig,
): QualityIssue[] {
  const severity = severityFor(config, "duplicate_product_id");
  const counts = new Map<string, number>();
  const firstCategory = new Map<string, string>();
  for (const row of rows) {
    counts.set(row.product_id, (counts.get(row.product_id) ?? 0) + 1);
    if (!firstCategory.has(row.product_id)) {
      firstCategory.set(row.product_id, categoryOf(row));
    }
  }
  const issues: QualityIssue[] = [];
  for (const [productId, count] of counts) {
    if (count > 1) {
      issues.push({
        rule_id: "duplicate_product_id",
        severity,
        product_id: productId,
        category: firstCategory.get(productId) ?? "",
        message: `Product ID appears ${count} times.`,
        details: { count },
      });
    }
  }
  return issues;
}

/** Rule 10 — price strictly below cost (explicit margin loss). */
export function detectPriceBelowCost(
  rows: NormalizedRow[],
  config: CatalogKitConfig,
): QualityIssue[] {
  const severity = severityFor(config, "price_below_cost");
  const issues: QualityIssue[] = [];
  for (const row of rows) {
    if (new Decimal(row.price).lt(new Decimal(row.cost))) {
      issues.push({
        rule_id: "price_below_cost",
        severity,
        product_id: row.product_id,
        category: categoryOf(row),
        message: "Price is below cost.",
        details: { price: row.price, cost: row.cost },
      });
    }
  }
  return issues;
}

/** A rule entry: stable id plus an executor that may consult `now`. */
interface RuleEntry {
  id: string;
  run: (rows: NormalizedRow[], config: CatalogKitConfig, now: Date) => QualityIssue[];
}

/** All rules in stable reporting order (mirrors the Python `RULES`). */
export const RULES: RuleEntry[] = [
  { id: "zero_price_nonzero_cost", run: (rows, config) => detectZeroPriceNonzeroCost(rows, config) },
  { id: "nonzero_price_zero_cost", run: (rows, config) => detectNonzeroPriceZeroCost(rows, config) },
  { id: "low_margin", run: (rows, config) => detectLowMargin(rows, config) },
  { id: "negative_margin", run: (rows, config) => detectNegativeMargin(rows, config) },
  { id: "negative_inventory", run: (rows, config) => detectNegativeInventory(rows, config) },
  { id: "stale_pricing", run: (rows, config, now) => detectStalePricing(rows, config, now) },
  { id: "missing_manufacturer", run: (rows, config) => detectMissingManufacturer(rows, config) },
  { id: "dirty_vendor_sku", run: (rows, config) => detectDirtyVendorSku(rows, config) },
  { id: "duplicate_product_id", run: (rows, config) => detectDuplicateProductId(rows, config) },
  { id: "price_below_cost", run: (rows, config) => detectPriceBelowCost(rows, config) },
];

/** Rule ids in stable reporting order. */
export const RULE_IDS: string[] = RULES.map((rule) => rule.id);

/** Human-readable descriptions for each rule (for the report UI). */
export const RULE_DESCRIPTIONS: Record<string, string> = {
  zero_price_nonzero_cost: "Price is $0 while cost is greater than $0.",
  nonzero_price_zero_cost: "Price is greater than $0 while cost is $0 (missing cost data).",
  low_margin: "Margin percent is below the configured threshold.",
  negative_margin: "Margin percent is negative (price below cost).",
  negative_inventory: "On-hand quantity is negative.",
  stale_pricing: "Pricing has not been updated within the configured month window.",
  missing_manufacturer: 'Manufacturer is blank or the catch-all "Other".',
  dirty_vendor_sku: "Vendor SKU has leading/trailing whitespace or non-printable characters.",
  duplicate_product_id: "Product ID appears on more than one row.",
  price_below_cost: "Price is strictly below cost.",
};

/**
 * Run every rule (when quality detection is enabled) and collect issues in
 * {@link RULES} order.
 *
 * @param rows Mapped rows to audit (the full input, before filtering).
 * @param config Resolved configuration.
 * @param now Reference time for the stale-pricing rule (defaults to the present).
 */
export function runQualityChecks(
  rows: NormalizedRow[],
  config: CatalogKitConfig,
  now: Date = new Date(),
): QualityIssue[] {
  if (!config.quality.enabled) return [];
  const issues: QualityIssue[] = [];
  for (const rule of RULES) {
    if (config.quality.rules[rule.id]?.enabled === false) continue;
    issues.push(...rule.run(rows, config, now));
  }
  return issues;
}
